import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular/messages';
import { TranslateService } from '@ngx-translate/core';
import { of, catchError, concatMap, map, switchMap, tap } from 'rxjs';
import { finalizeLoader } from '@app/shared/components';
import { ToastErrorService } from '@app/shared/services';
import { assessmentActions } from '../actions/assessment.actions';
import { HttpService } from '../services/http.service';

@Injectable()
export class AssessmentEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);
    private readonly toastErrorService = inject(ToastErrorService);

    getAssessment$ = createEffect(() => {
        return this.actions.pipe(
            ofType(assessmentActions.getAssessment),
            switchMap(({ courseId }) => {
                return this.httpService.getAssessment(courseId).pipe(
                    finalizeLoader('assessment-get'),
                    map((assessment) => assessmentActions.getAssessmentSuccess({ payload: assessment })),
                    catchError((response: HttpErrorResponse) => {
                        if (response.status === 404) {
                            return of(assessmentActions.getAssessmentSuccess({ payload: null }));
                        }

                        return of(assessmentActions.getAssessmentError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    saveAssessment$ = createEffect(() => {
        return this.actions.pipe(
            ofType(assessmentActions.saveAssessment),
            concatMap(({ courseId, value }) => {
                return this.httpService.createAssessment(courseId, value).pipe(
                    finalizeLoader('assessment-save'),
                    map((assessment) => assessmentActions.saveAssessmentSuccess({ payload: assessment })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.assessment.saved'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.assessment.saveError');
                        return of(assessmentActions.saveAssessmentError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    patchAssessment$ = createEffect(() => {
        return this.actions.pipe(
            ofType(assessmentActions.patchAssessment),
            concatMap(({ id, value }) => {
                return this.httpService.patchAssessment(id, value).pipe(
                    finalizeLoader('assessment-patch'),
                    map((assessment) => assessmentActions.patchAssessmentSuccess({ payload: assessment })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.assessment.saved'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.assessment.saveError');
                        return of(assessmentActions.patchAssessmentError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    deleteAssessment$ = createEffect(() => {
        return this.actions.pipe(
            ofType(assessmentActions.deleteAssessment),
            concatMap(({ id }) => {
                return this.httpService.deleteAssessment(id).pipe(
                    finalizeLoader('assessment-delete'),
                    map(() => assessmentActions.deleteAssessmentSuccess({ id })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.assessment.deleted'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.assessment.deleteError');
                        return of(assessmentActions.deleteAssessmentError({ code: response.status }));
                    }),
                );
            }),
        );
    });
}
